import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  Divider,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  IconButton,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  Switch,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  Favorite as HeartIcon,
  Thermostat as TempIcon,
  MonitorWeight as WeightIcon,
  Height as HeightIcon,
  Opacity as BloodIcon,
  Air as OxygenIcon,
  Speed as PulseIcon,
  LocalHospital as HospitalIcon,
  Medication as MedicationIcon,
  Note as NoteIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Send as SubmitIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import axios from 'axios';

interface MedicalExaminationFormProps {
  open: boolean;
  onClose: () => void;
  appointmentId: number;
  appointmentData?: {
    patientName: string;
    patientAge: number | null;
    patientGender: string | null;
    bookingNumber: string;
    chiefComplaints?: string;
    reasonForVisit?: string;
  };
  onSubmitSuccess: () => void;
}

interface Medication {
  medicineName: string;
  dosage: string;
  frequency: string;
  duration: string;
  instructions: string;
  quantity: number | null;
}

interface ExaminationData {
  // Vitals
  bloodPressureSystolic: number | null;
  bloodPressureDiastolic: number | null;
  pulseRate: number | null;
  temperature: number | null;
  temperatureUnit: string;
  bloodSugarFasting: number | null;
  bloodSugarPostMeal: number | null;
  bloodSugarRandom: number | null;
  weight: number | null;
  weightUnit: string;
  height: number | null;
  heightUnit: string;
  oxygenSaturation: number | null;
  respiratoryRate: number | null;
  bmi: number | null;

  // Symptoms
  hasFever: boolean;
  feverDetails: string;
  hasCough: boolean;
  coughDetails: string;
  hasBodyPain: boolean;
  bodyPainDetails: string;
  hasHeadache: boolean;
  headacheDetails: string;
  customSymptoms: string;
  chiefComplaints: string;
  symptomDuration: string;
  presentIllnessHistory: string;

  // Diagnosis
  provisionalDiagnosis: string;
  differentialDiagnosis: string;
  finalDiagnosis: string;
  icdCodes: string;
  assessmentNotes: string;
  clinicalFindings: string;
  generalExamination: string;
  systemicExamination: string;
  localExamination: string;

  // Treatment
  treatmentPlan: string;
  investigationsAdvised: string;
  proceduresPerformed: string;
  referralNotes: string;
  referralHospital: string;

  // Follow Up
  followUpRequired: boolean;
  followUpDate: string;
  followUpInstructions: string;

  // Emergency
  isEmergency: boolean;
  emergencyDetails: string;
  hospitalAdmissionRequired: boolean;
  admissionHospital: string;

  // Medical History
  pastMedicalHistory: string;
  familyHistory: string;
  allergies: string;
  currentMedications: string;

  // Additional
  notes: string;
  medications: Medication[];
}

const initialExaminationData: ExaminationData = {
  // Vitals
  bloodPressureSystolic: null,
  bloodPressureDiastolic: null,
  pulseRate: null,
  temperature: null,
  temperatureUnit: 'Fahrenheit',
  bloodSugarFasting: null,
  bloodSugarPostMeal: null,
  bloodSugarRandom: null,
  weight: null,
  weightUnit: 'kg',
  height: null,
  heightUnit: 'cm',
  oxygenSaturation: null,
  respiratoryRate: null,
  bmi: null,

  // Symptoms
  hasFever: false,
  feverDetails: '',
  hasCough: false,
  coughDetails: '',
  hasBodyPain: false,
  bodyPainDetails: '',
  hasHeadache: false,
  headacheDetails: '',
  customSymptoms: '',
  chiefComplaints: '',
  symptomDuration: '',
  presentIllnessHistory: '',

  // Diagnosis
  provisionalDiagnosis: '',
  differentialDiagnosis: '',
  finalDiagnosis: '',
  icdCodes: '',
  assessmentNotes: '',
  clinicalFindings: '',
  generalExamination: '',
  systemicExamination: '',
  localExamination: '',

  // Treatment
  treatmentPlan: '',
  investigationsAdvised: '',
  proceduresPerformed: '',
  referralNotes: '',
  referralHospital: '',

  // Follow Up
  followUpRequired: false,
  followUpDate: '',
  followUpInstructions: '',

  // Emergency
  isEmergency: false,
  emergencyDetails: '',
  hospitalAdmissionRequired: false,
  admissionHospital: '',

  // Medical History
  pastMedicalHistory: '',
  familyHistory: '',
  allergies: '',
  currentMedications: '',

  // Additional
  notes: '',
  medications: [],
};

/**
 * Medical Examination Form Component
 * Structured form for doctors to capture patient examination data.
 *
 * @author Civilhealth Development Team
 */
const MedicalExaminationForm: React.FC<MedicalExaminationFormProps> = ({
  open,
  onClose,
  appointmentId,
  appointmentData,
  onSubmitSuccess,
}) => {
  const [formData, setFormData] = useState<ExaminationData>(initialExaminationData);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingReportId, setExistingReportId] = useState<number | null>(null);
  const [activeSection, setActiveSection] = useState<string>('vitals');

  // Load existing examination data
  useEffect(() => {
    if (open && appointmentId) {
      loadExaminationData();
    }
  }, [open, appointmentId]);

  // Calculate BMI when weight/height changes
  useEffect(() => {
    if (formData.weight && formData.height && formData.height > 0) {
      const heightInMeters = formData.heightUnit === 'cm' ? formData.height / 100 : formData.height * 0.0254;
      const bmi = Math.round((formData.weight / (heightInMeters * heightInMeters)) * 100) / 100;
      setFormData(prev => ({ ...prev, bmi }));
    }
  }, [formData.weight, formData.height, formData.weightUnit, formData.heightUnit]);

  const loadExaminationData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/medical-examination/appointment/${appointmentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setExistingReportId(data.id);
        setFormData({
          ...initialExaminationData,
          ...data,
          medications: data.medications || [],
        });
      }
    } catch (err: any) {
      // No existing report - start fresh
      if (err.response?.status !== 404) {
        console.log('No existing examination found, starting fresh');
      }
      // Pre-fill chief complaints from appointment
      setFormData(prev => ({
        ...prev,
        chiefComplaints: appointmentData?.chiefComplaints || appointmentData?.reasonForVisit || '',
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof ExaminationData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleMedicationChange = (index: number, field: keyof Medication, value: any) => {
    setFormData(prev => {
      const medications = [...prev.medications];
      medications[index] = { ...medications[index], [field]: value };
      return { ...prev, medications };
    });
  };

  const addMedication = () => {
    setFormData(prev => ({
      ...prev,
      medications: [...prev.medications, {
        medicineName: '',
        dosage: '',
        frequency: '',
        duration: '',
        instructions: '',
        quantity: null,
      }],
    }));
  };

  const removeMedication = (index: number) => {
    setFormData(prev => ({
      ...prev,
      medications: prev.medications.filter((_, i) => i !== index),
    }));
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/medical-examination`,
        {
          appointmentId,
          ...formData,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Show success message
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save examination');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.finalDiagnosis) {
      setError('Final Diagnosis is required to submit the examination');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('accessToken');

      // First save the examination
      const saveResponse = await axios.post(
        `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/medical-examination`,
        {
          appointmentId,
          ...formData,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Then submit it
      if (saveResponse.data.success && saveResponse.data.data.id) {
        await axios.post(
          `${import.meta.env.VITE_API_URL || 'http://localhost:8082/api'}/v1/medical-examination/${saveResponse.data.data.id}/submit`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      onSubmitSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to submit examination');
    } finally {
      setSaving(false);
    }
  };

  // Get vital status color
  const getVitalStatus = (type: string, value: number | null): 'normal' | 'warning' | 'danger' | 'unknown' => {
    if (value === null) return 'unknown';

    switch (type) {
      case 'bpSystolic':
        if (value < 90) return 'danger';
        if (value <= 120) return 'normal';
        if (value <= 140) return 'warning';
        return 'danger';
      case 'bpDiastolic':
        if (value < 60) return 'danger';
        if (value <= 80) return 'normal';
        if (value <= 90) return 'warning';
        return 'danger';
      case 'pulse':
        if (value < 60) return 'warning';
        if (value <= 100) return 'normal';
        return 'warning';
      case 'temperature':
        // Fahrenheit
        if (value < 97) return 'warning';
        if (value <= 99) return 'normal';
        return 'warning';
      case 'oxygen':
        if (value >= 95) return 'normal';
        if (value >= 90) return 'warning';
        return 'danger';
      case 'sugarFasting':
        if (value < 70) return 'danger';
        if (value <= 100) return 'normal';
        if (value <= 125) return 'warning';
        return 'danger';
      case 'sugarRandom':
        if (value < 70) return 'danger';
        if (value <= 140) return 'normal';
        if (value <= 200) return 'warning';
        return 'danger';
      default:
        return 'unknown';
    }
  };

  const getStatusColor = (status: 'normal' | 'warning' | 'danger' | 'unknown') => {
    switch (status) {
      case 'normal': return 'success.main';
      case 'warning': return 'warning.main';
      case 'danger': return 'error.main';
      default: return 'text.secondary';
    }
  };

  const sections = [
    { id: 'vitals', label: 'Vitals', icon: <HeartIcon /> },
    { id: 'symptoms', label: 'Symptoms', icon: <NoteIcon /> },
    { id: 'diagnosis', label: 'Diagnosis', icon: <HospitalIcon /> },
    { id: 'treatment', label: 'Treatment', icon: <MedicationIcon /> },
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      {loading && <LinearProgress />}

      <DialogTitle sx={{
        bgcolor: 'transparent' ,
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <HospitalIcon />
          <Box>
            <Typography variant="h6">Medical Examination Form</Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {appointmentData?.patientName} | {appointmentData?.bookingNumber}
            </Typography>
          </Box>
        </Box>
        <Chip
          label={`${appointmentData?.patientAge || '-'} yrs | ${appointmentData?.patientGender || '-'}`}
          sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}
        />
      </DialogTitle>

      <DialogContent sx={{ p: 0, display: 'flex' }}>
        {/* Left Sidebar - Section Navigation */}
        {/* HIGH CONTRAST: Light background + Dark text */}
        <Box sx={{ width: 200, bgcolor: 'transparent' , borderRight: '1px solid #e0e0e0', p: 1 }}>
          {sections.map((section) => (
            <Button
              key={section.id}
              fullWidth
              onClick={() => setActiveSection(section.id)}
              sx={{
                justifyContent: 'flex-start',
                mb: 1,
                bgcolor: activeSection === section.id ? '#EFF6FF' : 'transparent',
                color: activeSection === section.id ? '#1D4ED8' : '#5f6368',
                '&:hover': { bgcolor: 'transparent' },
              }}
              startIcon={section.icon}
            >
              {section.label}
            </Button>
          ))}
        </Box>

        {/* Main Content */}
        <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {/* VITALS SECTION */}
          {activeSection === 'vitals' && (
            <Box>
              <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <HeartIcon color="error" />
                Vital Signs
              </Typography>

              <Grid container spacing={3}>
                {/* Blood Pressure */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'transparent' }}>
                        <BloodIcon sx={{ color: '#c62828' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Blood Pressure</Typography>
                        <Typography variant="caption" color="text.secondary">mmHg</Typography>
                      </Box>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Systolic"
                          value={formData.bloodPressureSystolic || ''}
                          onChange={(e) => handleInputChange('bloodPressureSystolic', e.target.value ? parseInt(e.target.value) : null)}
                          InputProps={{
                            endAdornment: (
                              <Box sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: getStatusColor(getVitalStatus('bpSystolic', formData.bloodPressureSystolic))
                              }} />
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Diastolic"
                          value={formData.bloodPressureDiastolic || ''}
                          onChange={(e) => handleInputChange('bloodPressureDiastolic', e.target.value ? parseInt(e.target.value) : null)}
                          InputProps={{
                            endAdornment: (
                              <Box sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                bgcolor: getStatusColor(getVitalStatus('bpDiastolic', formData.bloodPressureDiastolic))
                              }} />
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Pulse Rate */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'transparent' }}>
                        <PulseIcon sx={{ color: '#2e7d32' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Pulse Rate</Typography>
                        <Typography variant="caption" color="text.secondary">beats per minute</Typography>
                      </Box>
                    </Box>
                    <TextField
                      fullWidth
                      type="number"
                      label="Pulse Rate"
                      value={formData.pulseRate || ''}
                      onChange={(e) => handleInputChange('pulseRate', e.target.value ? parseInt(e.target.value) : null)}
                      InputProps={{
                        endAdornment: <Typography variant="body2" color="text.secondary">bpm</Typography>,
                      }}
                    />
                  </Paper>
                </Grid>

                {/* Temperature */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'transparent' }}>
                        <TempIcon sx={{ color: '#ef6c00' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Temperature</Typography>
                        <Typography variant="caption" color="text.secondary">Body temperature</Typography>
                      </Box>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={8}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Temperature"
                          value={formData.temperature || ''}
                          onChange={(e) => handleInputChange('temperature', e.target.value ? parseFloat(e.target.value) : null)}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl fullWidth>
                          <InputLabel>Unit</InputLabel>
                          <Select
                            value={formData.temperatureUnit}
                            label="Unit"
                            onChange={(e) => handleInputChange('temperatureUnit', e.target.value)}
                          >
                            <MenuItem value="Fahrenheit">°F</MenuItem>
                            <MenuItem value="Celsius">°C</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Oxygen Saturation */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'transparent' }}>
                        <OxygenIcon sx={{ color: '#1565c0' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Oxygen Saturation</Typography>
                        <Typography variant="caption" color="text.secondary">SpO2 percentage</Typography>
                      </Box>
                    </Box>
                    <TextField
                      fullWidth
                      type="number"
                      label="SpO2"
                      value={formData.oxygenSaturation || ''}
                      onChange={(e) => handleInputChange('oxygenSaturation', e.target.value ? parseInt(e.target.value) : null)}
                      InputProps={{
                        endAdornment: <Typography variant="body2" color="text.secondary">%</Typography>,
                      }}
                    />
                  </Paper>
                </Grid>

                {/* Blood Sugar */}
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'transparent' }}>
                        <BloodIcon sx={{ color: '#ad1457' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Blood Sugar</Typography>
                        <Typography variant="caption" color="text.secondary">mg/dL</Typography>
                      </Box>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Fasting"
                          value={formData.bloodSugarFasting || ''}
                          onChange={(e) => handleInputChange('bloodSugarFasting', e.target.value ? parseInt(e.target.value) : null)}
                          placeholder="Before meal"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Post Meal"
                          value={formData.bloodSugarPostMeal || ''}
                          onChange={(e) => handleInputChange('bloodSugarPostMeal', e.target.value ? parseInt(e.target.value) : null)}
                          placeholder="After meal"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Random"
                          value={formData.bloodSugarRandom || ''}
                          onChange={(e) => handleInputChange('bloodSugarRandom', e.target.value ? parseInt(e.target.value) : null)}
                          placeholder="Any time"
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* Weight & Height */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'transparent' }}>
                        <WeightIcon sx={{ color: '#7b1fa2' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Weight</Typography>
                        <Typography variant="caption" color="text.secondary">Body weight</Typography>
                      </Box>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={8}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Weight"
                          value={formData.weight || ''}
                          onChange={(e) => handleInputChange('weight', e.target.value ? parseFloat(e.target.value) : null)}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl fullWidth>
                          <InputLabel>Unit</InputLabel>
                          <Select
                            value={formData.weightUnit}
                            label="Unit"
                            onChange={(e) => handleInputChange('weightUnit', e.target.value)}
                          >
                            <MenuItem value="kg">kg</MenuItem>
                            <MenuItem value="lb">lb</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ bgcolor: 'transparent' }}>
                        <HeightIcon sx={{ color: '#00695c' }} />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Height</Typography>
                        <Typography variant="caption" color="text.secondary">Body height</Typography>
                      </Box>
                    </Box>
                    <Grid container spacing={2}>
                      <Grid item xs={8}>
                        <TextField
                          fullWidth
                          type="number"
                          label="Height"
                          value={formData.height || ''}
                          onChange={(e) => handleInputChange('height', e.target.value ? parseFloat(e.target.value) : null)}
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <FormControl fullWidth>
                          <InputLabel>Unit</InputLabel>
                          <Select
                            value={formData.heightUnit}
                            label="Unit"
                            onChange={(e) => handleInputChange('heightUnit', e.target.value)}
                          >
                            <MenuItem value="cm">cm</MenuItem>
                            <MenuItem value="inch">inch</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>

                {/* BMI Display */}
                {formData.bmi && (
                  <Grid item xs={12}>
                    <Alert
                      severity={formData.bmi < 18.5 ? 'warning' : formData.bmi <= 24.9 ? 'success' : formData.bmi <= 29.9 ? 'warning' : 'error'}
                      sx={{ borderRadius: 2 }}
                    >
                      <Typography variant="subtitle2">
                        BMI: {formData.bmi} kg/m²
                        {formData.bmi < 18.5 && ' - Underweight'}
                        {formData.bmi >= 18.5 && formData.bmi <= 24.9 && ' - Normal'}
                        {formData.bmi >= 25 && formData.bmi <= 29.9 && ' - Overweight'}
                        {formData.bmi >= 30 && ' - Obese'}
                      </Typography>
                    </Alert>
                  </Grid>
                )}

                {/* Respiratory Rate */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Respiratory Rate"
                    value={formData.respiratoryRate || ''}
                    onChange={(e) => handleInputChange('respiratoryRate', e.target.value ? parseInt(e.target.value) : null)}
                    InputProps={{
                      endAdornment: <Typography variant="body2" color="text.secondary">breaths/min</Typography>,
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* SYMPTOMS SECTION */}
          {activeSection === 'symptoms' && (
            <Box>
              <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <NoteIcon color="primary" />
                Symptoms & Clinical Findings
              </Typography>

              {/* Chief Complaints */}
              <Paper sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: 'transparent' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Chief Complaints</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.chiefComplaints}
                  onChange={(e) => handleInputChange('chiefComplaints', e.target.value)}
                  placeholder="Patient's main complaints..."
                />
              </Paper>

              {/* Structured Symptoms */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Symptom Checklist</Typography>
              <Grid container spacing={2}>
                {/* Fever */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, borderRadius: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.hasFever}
                          onChange={(e) => handleInputChange('hasFever', e.target.checked)}
                          color="error"
                        />
                      }
                      label={<Typography sx={{ fontWeight: 600 }}>Fever</Typography>}
                    />
                    {formData.hasFever && (
                      <TextField
                        fullWidth
                        size="small"
                        label="Details (duration, pattern, etc.)"
                        value={formData.feverDetails}
                        onChange={(e) => handleInputChange('feverDetails', e.target.value)}
                        sx={{ mt: 1 }}
                        placeholder="e.g., 3 days, high grade, evening rise"
                      />
                    )}
                  </Paper>
                </Grid>

                {/* Cough */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, borderRadius: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.hasCough}
                          onChange={(e) => handleInputChange('hasCough', e.target.checked)}
                          color="primary"
                        />
                      }
                      label={<Typography sx={{ fontWeight: 600 }}>Cough</Typography>}
                    />
                    {formData.hasCough && (
                      <TextField
                        fullWidth
                        size="small"
                        label="Details (type, duration, etc.)"
                        value={formData.coughDetails}
                        onChange={(e) => handleInputChange('coughDetails', e.target.value)}
                        sx={{ mt: 1 }}
                        placeholder="e.g., Dry/Productive, 5 days"
                      />
                    )}
                  </Paper>
                </Grid>

                {/* Body Pain */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, borderRadius: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.hasBodyPain}
                          onChange={(e) => handleInputChange('hasBodyPain', e.target.checked)}
                          color="warning"
                        />
                      }
                      label={<Typography sx={{ fontWeight: 600 }}>Body Pain</Typography>}
                    />
                    {formData.hasBodyPain && (
                      <TextField
                        fullWidth
                        size="small"
                        label="Details (location, severity, etc.)"
                        value={formData.bodyPainDetails}
                        onChange={(e) => handleInputChange('bodyPainDetails', e.target.value)}
                        sx={{ mt: 1 }}
                        placeholder="e.g., Lower back, moderate, 2 days"
                      />
                    )}
                  </Paper>
                </Grid>

                {/* Headache */}
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, borderRadius: 2 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.hasHeadache}
                          onChange={(e) => handleInputChange('hasHeadache', e.target.checked)}
                          color="secondary"
                        />
                      }
                      label={<Typography sx={{ fontWeight: 600 }}>Headache</Typography>}
                    />
                    {formData.hasHeadache && (
                      <TextField
                        fullWidth
                        size="small"
                        label="Details (type, location, etc.)"
                        value={formData.headacheDetails}
                        onChange={(e) => handleInputChange('headacheDetails', e.target.value)}
                        sx={{ mt: 1 }}
                        placeholder="e.g., Frontal, throbbing, 1 day"
                      />
                    )}
                  </Paper>
                </Grid>
              </Grid>

              {/* Custom Symptoms */}
              <Paper sx={{ p: 2, mt: 3, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Other Symptoms</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.customSymptoms}
                  onChange={(e) => handleInputChange('customSymptoms', e.target.value)}
                  placeholder="Any other symptoms not listed above..."
                />
              </Paper>

              {/* Symptom Duration */}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Symptom Duration"
                    value={formData.symptomDuration}
                    onChange={(e) => handleInputChange('symptomDuration', e.target.value)}
                    placeholder="e.g., 3 days, 1 week"
                  />
                </Grid>
              </Grid>

              {/* History of Present Illness */}
              <Paper sx={{ p: 2, mt: 3, borderRadius: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>History of Present Illness</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  value={formData.presentIllnessHistory}
                  onChange={(e) => handleInputChange('presentIllnessHistory', e.target.value)}
                  placeholder="Detailed history of current illness..."
                />
              </Paper>

              {/* Medical History */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 2 }}>Medical History</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Past Medical History"
                    value={formData.pastMedicalHistory}
                    onChange={(e) => handleInputChange('pastMedicalHistory', e.target.value)}
                    placeholder="Previous illnesses, surgeries..."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Family History"
                    value={formData.familyHistory}
                    onChange={(e) => handleInputChange('familyHistory', e.target.value)}
                    placeholder="Relevant family medical history..."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Allergies"
                    value={formData.allergies}
                    onChange={(e) => handleInputChange('allergies', e.target.value)}
                    placeholder="Known allergies..."
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Current Medications"
                    value={formData.currentMedications}
                    onChange={(e) => handleInputChange('currentMedications', e.target.value)}
                    placeholder="Current medications..."
                  />
                </Grid>
              </Grid>

              {/* Examination Findings */}
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mt: 3, mb: 2 }}>Examination Findings</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="General Examination"
                    value={formData.generalExamination}
                    onChange={(e) => handleInputChange('generalExamination', e.target.value)}
                    placeholder="General appearance, build, nutrition..."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Systemic Examination"
                    value={formData.systemicExamination}
                    onChange={(e) => handleInputChange('systemicExamination', e.target.value)}
                    placeholder="CVS, RS, CNS, PA examination..."
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Local Examination"
                    value={formData.localExamination}
                    onChange={(e) => handleInputChange('localExamination', e.target.value)}
                    placeholder="Local findings if any..."
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {/* DIAGNOSIS SECTION */}
          {activeSection === 'diagnosis' && (
            <Box>
              <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <HospitalIcon color="success" />
                Diagnosis
              </Typography>

              {/* Diagnosis Fields */}
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'transparent' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Provisional Diagnosis
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      value={formData.provisionalDiagnosis}
                      onChange={(e) => handleInputChange('provisionalDiagnosis', e.target.value)}
                      placeholder="Initial diagnosis based on symptoms..."
                    />
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'transparent' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                      Differential Diagnosis
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      value={formData.differentialDiagnosis}
                      onChange={(e) => handleInputChange('differentialDiagnosis', e.target.value)}
                      placeholder="Other possible diagnoses..."
                    />
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'transparent' , border: '2px solid #ef5350' }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'error.main' }}>
                      Final Diagnosis *
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      required
                      value={formData.finalDiagnosis}
                      onChange={(e) => handleInputChange('finalDiagnosis', e.target.value)}
                      placeholder="Final confirmed diagnosis..."
                      error={!formData.finalDiagnosis}
                      helperText={!formData.finalDiagnosis ? 'Final diagnosis is required to submit' : ''}
                    />
                  </Paper>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="ICD Codes"
                    value={formData.icdCodes}
                    onChange={(e) => handleInputChange('icdCodes', e.target.value)}
                    placeholder="e.g., J06.9, K29.70"
                    helperText="Enter ICD-10 codes separated by comma"
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Clinical Findings"
                    value={formData.clinicalFindings}
                    onChange={(e) => handleInputChange('clinicalFindings', e.target.value)}
                    placeholder="Detailed clinical findings..."
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Assessment Notes"
                    value={formData.assessmentNotes}
                    onChange={(e) => handleInputChange('assessmentNotes', e.target.value)}
                    placeholder="Doctor's assessment and notes..."
                  />
                </Grid>
              </Grid>

              {/* Emergency Section */}
              <Typography variant="h6" sx={{ mt: 4, mb: 2, color: 'error.main' }}>
                <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Emergency Information
              </Typography>
              <Paper sx={{ p: 2, borderRadius: 2, border: formData.isEmergency ? '2px solid #ef5350' : 'none' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isEmergency}
                          onChange={(e) => handleInputChange('isEmergency', e.target.checked)}
                          color="error"
                        />
                      }
                      label={<Typography sx={{ fontWeight: 600 }}>Mark as Emergency</Typography>}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.hospitalAdmissionRequired}
                          onChange={(e) => handleInputChange('hospitalAdmissionRequired', e.target.checked)}
                          color="warning"
                        />
                      }
                      label={<Typography sx={{ fontWeight: 600 }}>Hospital Admission Required</Typography>}
                    />
                  </Grid>
                  {formData.isEmergency && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Emergency Details"
                        value={formData.emergencyDetails}
                        onChange={(e) => handleInputChange('emergencyDetails', e.target.value)}
                        placeholder="Emergency case details..."
                      />
                    </Grid>
                  )}
                  {formData.hospitalAdmissionRequired && (
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Admission Hospital"
                        value={formData.admissionHospital}
                        onChange={(e) => handleInputChange('admissionHospital', e.target.value)}
                        placeholder="Hospital name for admission..."
                      />
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Box>
          )}

          {/* TREATMENT SECTION */}
          {activeSection === 'treatment' && (
            <Box>
              <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                <MedicationIcon color="primary" />
                Treatment & Medications
              </Typography>

              {/* Treatment Plan */}
              <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Treatment Plan</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.treatmentPlan}
                  onChange={(e) => handleInputChange('treatmentPlan', e.target.value)}
                  placeholder="Treatment plan and advice..."
                />
              </Paper>

              {/* Medications */}
              <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Prescribed Medications</Typography>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addMedication}
                    size="small"
                  >
                    Add Medication
                  </Button>
                </Box>

                {formData.medications.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                    No medications added. Click "Add Medication" to prescribe.
                  </Typography>
                ) : (
                  formData.medications.map((med, index) => (
                    <Card key={index} sx={{ mb: 2, bgcolor: 'transparent' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Medication #{index + 1}
                          </Typography>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeMedication(index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Medicine Name *"
                              value={med.medicineName}
                              onChange={(e) => handleMedicationChange(index, 'medicineName', e.target.value)}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Dosage"
                              value={med.dosage}
                              onChange={(e) => handleMedicationChange(index, 'dosage', e.target.value)}
                              placeholder="e.g., 500mg"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Frequency</InputLabel>
                              <Select
                                value={med.frequency}
                                label="Frequency"
                                onChange={(e) => handleMedicationChange(index, 'frequency', e.target.value)}
                              >
                                <MenuItem value="OD">Once daily (OD)</MenuItem>
                                <MenuItem value="BD">Twice daily (BD)</MenuItem>
                                <MenuItem value="TDS">Three times daily (TDS)</MenuItem>
                                <MenuItem value="QDS">Four times daily (QDS)</MenuItem>
                                <MenuItem value="SOS">As needed (SOS)</MenuItem>
                                <MenuItem value="STAT">Single dose (STAT)</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Duration"
                              value={med.duration}
                              onChange={(e) => handleMedicationChange(index, 'duration', e.target.value)}
                              placeholder="e.g., 5 days"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Quantity"
                              value={med.quantity || ''}
                              onChange={(e) => handleMedicationChange(index, 'quantity', e.target.value ? parseInt(e.target.value) : null)}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              size="small"
                              label="Instructions"
                              value={med.instructions}
                              onChange={(e) => handleMedicationChange(index, 'instructions', e.target.value)}
                              placeholder="e.g., Take after meals"
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))
                )}
              </Paper>

              {/* Investigations */}
              <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Investigations Advised</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.investigationsAdvised}
                  onChange={(e) => handleInputChange('investigationsAdvised', e.target.value)}
                  placeholder="Lab tests, imaging studies..."
                />
              </Paper>

              {/* Procedures */}
              <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Procedures Performed</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.proceduresPerformed}
                  onChange={(e) => handleInputChange('proceduresPerformed', e.target.value)}
                  placeholder="Any procedures performed during visit..."
                />
              </Paper>

              {/* Referral */}
              <Paper sx={{ p: 2, mb: 3, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Referral</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Referral Hospital"
                      value={formData.referralHospital}
                      onChange={(e) => handleInputChange('referralHospital', e.target.value)}
                      placeholder="Hospital name if referring..."
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Referral Notes"
                      value={formData.referralNotes}
                      onChange={(e) => handleInputChange('referralNotes', e.target.value)}
                      placeholder="Referral reason and notes..."
                    />
                  </Grid>
                </Grid>
              </Paper>

              {/* Follow Up */}
              <Paper sx={{ p: 2, mb: 3, borderRadius: 2, border: formData.followUpRequired ? '2px solid #1a73e8' : 'none' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.followUpRequired}
                        onChange={(e) => handleInputChange('followUpRequired', e.target.checked)}
                        color="primary"
                      />
                    }
                    label={<Typography sx={{ fontWeight: 600 }}>Follow Up Required</Typography>}
                  />
                </Box>
                {formData.followUpRequired && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Follow Up Date"
                        value={formData.followUpDate}
                        onChange={(e) => handleInputChange('followUpDate', e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        multiline
                        rows={2}
                        label="Follow Up Instructions"
                        value={formData.followUpInstructions}
                        onChange={(e) => handleInputChange('followUpInstructions', e.target.value)}
                        placeholder="Instructions for follow up..."
                      />
                    </Grid>
                  </Grid>
                )}
              </Paper>

              {/* Additional Notes */}
              <Paper sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>Additional Notes</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Any additional notes..."
                />
              </Paper>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, bgcolor: 'transparent' , borderTop: '1px solid #e0e0e0' }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={handleSaveDraft}
          disabled={saving}
        >
          Save Draft
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<SubmitIcon />}
          onClick={handleSubmit}
          disabled={saving || !formData.finalDiagnosis}
        >
          {saving ? 'Submitting...' : 'Submit Examination'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MedicalExaminationForm;
